import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { updateProfile, uploadImageToR2 } from '../api/client';
import { ArrowLeft, Camera, Loader2 } from 'lucide-react';
import { UserAvatar } from '../components/UserAvatar';

export function EditProfilePage() {
  useDocumentTitle('Edit Profile');
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [username, setUsername] = useState(user?.username || '');
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl || '');
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    try {
      setIsUploading(true);
      setError(null);
      const url = await uploadImageToR2(file, 'avatars');
      setAvatarUrl(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Validate username
    if (username.length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setError('Username can only contain letters, numbers, and underscores');
      return;
    }

    try {
      setIsSaving(true);
      await updateProfile({
        username: username !== user?.username ? username : undefined,
        displayName: displayName,
        avatarUrl: avatarUrl || undefined,
        bio: bio || undefined,
      });
      await refreshUser();
      setSuccess(true);
      setTimeout(() => navigate('/account'), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.content}>
          <button onClick={() => navigate('/account')} style={styles.backButton}>
            <ArrowLeft size={20} />
            <span>Back to Account</span>
          </button>

          <h1 style={styles.title}>Edit Profile</h1>

          <form onSubmit={handleSubmit} style={styles.form}>
            {/* Avatar Upload */}
            <div style={styles.avatarSection}>
              <div style={styles.avatarWrapper} onClick={handleAvatarClick}>
                <UserAvatar src={avatarUrl} alt="Your avatar" size={96} />
                <div style={styles.avatarOverlay}>
                  {isUploading ? (
                    <Loader2 size={24} color={colors.white} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <Camera size={24} color={colors.white} />
                  )}
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <p style={styles.avatarHint}>Click to upload a new photo</p>
            </div>

            {/* Username */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Username</label>
              <div style={styles.inputWithPrefix}>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase())}
                  placeholder="username"
                  style={styles.inputWithPrefixField}
                  maxLength={50}
                />
              </div>
              <p style={styles.hint}>
                Letters, numbers, and underscores only. This is your unique handle.
              </p>
            </div>

            {/* Display Name */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Display Name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your display name"
                style={styles.input}
                maxLength={100}
              />
              <p style={styles.hint}>
                This is how your name appears on your profile.
              </p>
            </div>

            {/* Bio */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Bio</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell people a little about yourself..."
                style={styles.textarea}
                maxLength={500}
                rows={4}
              />
              <p style={styles.charCount}>{bio.length}/500</p>
            </div>

            {error && <p style={styles.error}>{error}</p>}
            {success && <p style={styles.success}>Profile updated successfully!</p>}

            <button
              type="submit"
              style={styles.submitButton}
              disabled={isSaving || isUploading}
            >
              {isSaving ? (
                <>
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
  },
  content: {
    maxWidth: '500px',
    margin: '0 auto',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[600],
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    marginBottom: '16px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: colors.ink.black,
    marginBottom: '24px',
  },
  form: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    padding: '24px',
  },
  avatarSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '24px',
  },
  avatarWrapper: {
    position: 'relative',
    width: '100px',
    height: '100px',
    cursor: 'pointer',
  },
  avatar: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '50%',
  },
  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '36px',
    fontWeight: 600,
    backgroundColor: colors.brand.blue,
    color: colors.white,
    borderRadius: '50%',
  },
  avatarOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    borderRadius: '50%',
    opacity: 0,
    transition: 'opacity 0.2s',
  },
  avatarHint: {
    marginTop: '8px',
    fontSize: '13px',
    color: colors.gray[600],
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    fontSize: '16px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '10px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  inputWithPrefix: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '10px',
    overflow: 'hidden',
  },
  inputPrefix: {
    padding: '12px 0 12px 14px',
    fontSize: '16px',
    color: colors.gray[600],
    backgroundColor: colors.gray[100],
    borderRight: `1px solid ${colors.gray[300]}`,
    paddingRight: '10px',
  },
  inputWithPrefixField: {
    flex: 1,
    padding: '12px 14px',
    fontSize: '16px',
    border: 'none',
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    fontSize: '16px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '10px',
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  hint: {
    marginTop: '6px',
    fontSize: '12px',
    color: colors.gray[600],
  },
  charCount: {
    marginTop: '6px',
    fontSize: '12px',
    color: colors.gray[400],
    textAlign: 'right',
  },
  error: {
    color: '#DC2626',
    fontSize: '14px',
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#FEE2E2',
    borderRadius: '8px',
  },
  success: {
    color: '#16A34A',
    fontSize: '14px',
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#DCFCE7',
    borderRadius: '8px',
  },
  submitButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '14px 20px',
    fontSize: '16px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
};
